if (!customElements.get('lello-clean-product')) {
  customElements.define('lello-clean-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.listenerOptions = { signal: this.controller.signal };
      this.form = this.querySelector('[data-lc-product-form]');
      this.variantInput = this.querySelector('[data-lc-variant]');
      this.quantityInput = this.querySelector('[data-lc-quantity-input]');
      this.submit = this.querySelector('[data-lc-submit]');
      this.planSelect = this.querySelector('[data-lc-plan]');
      this.sticky = this.querySelector('[data-lc-sticky]');
      this.stickySubmit = this.querySelector('[data-lc-sticky-submit]');
      this.cartOpen = false;
      this.submitVisible = true;
      this.variants = JSON.parse(this.querySelector('[data-lc-variants]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-lc-product-strings]').textContent);
      this.moneyFormatter = new Intl.NumberFormat(this.dataset.locale || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      });

      this.addEventListener('click', (event) => this.onClick(event), this.listenerOptions);
      this.variantInput.addEventListener('change', () => this.updateVariant(true), this.listenerOptions);
      this.quantityInput.addEventListener('change', () => {
        this.normalizeQuantity();
        this.updateSticky();
      }, this.listenerOptions);
      this.quantityInput.addEventListener('input', () => this.updateSticky(), this.listenerOptions);
      this.planSelect?.addEventListener('change', () => this.updateVariant(true), this.listenerOptions);
      this.form.addEventListener('lc:product-form-ready', () => this.updateVariant(false), this.listenerOptions);
      document.addEventListener('lc:cart-open', () => {
        this.cartOpen = true;
        this.updateSticky();
      }, this.listenerOptions);
      document.addEventListener('lc:cart-close', () => {
        this.cartOpen = false;
        this.updateSticky();
      }, this.listenerOptions);
      window.addEventListener('pageshow', () => this.updateVariant(false), this.listenerOptions);
      window.addEventListener('popstate', () => this.restoreFromUrl(), this.listenerOptions);

      this.setupRecipient();
      this.updateVariant(false);
      this.observePurchaseButton();
      this.submitObserver = new MutationObserver(() => this.updateSticky());
      this.submitObserver.observe(this.submit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy', 'data-lc-cart-loading'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.intersectionObserver?.disconnect();
      this.submitObserver?.disconnect();
      this.initialized = false;
    }

    money(cents) {
      return this.moneyFormatter.format(Number(cents || 0) / 100);
    }

    selectedVariant() {
      return this.variants.find((variant) => String(variant.id) === this.variantInput.value);
    }

    selectedAllocation(variant) {
      if (!this.planSelect?.value) return null;
      return (variant.sellingPlanAllocations || []).find((allocation) =>
        String(allocation.selling_plan?.id || allocation.selling_plan_id) === this.planSelect.value
      ) || null;
    }

    updatePlans(variant) {
      if (!this.planSelect) return;
      const allocations = variant.sellingPlanAllocations || [];
      const availablePlans = new Set(allocations.map((allocation) => String(allocation.selling_plan?.id || allocation.selling_plan_id)));
      Array.from(this.planSelect.options).forEach((option) => {
        option.disabled = Boolean(option.value) && !availablePlans.has(option.value);
        option.hidden = option.disabled;
      });
      const selectedOption = this.planSelect.selectedOptions[0];
      if (!selectedOption || selectedOption.disabled) {
        const firstAvailable = Array.from(this.planSelect.options).find((option) => !option.disabled);
        this.planSelect.value = firstAvailable?.value || '';
      }
    }

    updateVariant(updateUrl) {
      const variant = this.selectedVariant();
      if (!variant) return;
      this.updatePlans(variant);
      const allocation = this.selectedAllocation(variant);
      this.currentPrice = allocation ? allocation.price : variant.price;
      this.querySelector('[data-lc-price]').textContent = allocation ? this.money(this.currentPrice) : variant.priceText;
      const compare = this.querySelector('[data-lc-compare]');
      const compareAmount = allocation?.compare_at_price || variant.compareAtPrice || 0;
      compare.hidden = !(compareAmount > this.currentPrice);
      compare.textContent = allocation ? this.money(compareAmount) : variant.compareAtText;
      const unitPrice = this.querySelector('[data-lc-unit-price]');
      const measure = variant.unitPriceMeasurement;
      unitPrice.hidden = !measure || !variant.unitPrice;
      if (measure && variant.unitPrice) {
        unitPrice.textContent = `${this.money(variant.unitPrice)} / ${measure.reference_value === 1 ? '' : measure.reference_value}${measure.reference_unit}`;
      }

      const rule = variant.quantityRule || {};
      this.quantityInput.min = rule.min || 1;
      this.quantityInput.step = rule.increment || 1;
      if (rule.max) this.quantityInput.max = rule.max;
      else this.quantityInput.removeAttribute('max');
      this.normalizeQuantity();
      const planRequired = this.planSelect?.dataset.requiresPlan === 'true';
      const available = variant.available && (!planRequired || Boolean(allocation));
      this.form.dataset.lcVariantAvailable = String(available);
      if (!this.form.hasAttribute('data-lc-adding')) {
        this.submit.disabled = !available;
        this.querySelector('[data-lc-submit-label]').textContent = available ? this.strings.addToCart : this.strings.soldOut;
      }

      if (this.lastVariantId !== variant.id && variant.mediaId) this.showMedia(variant.mediaId);
      this.lastVariantId = variant.id;
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        if (this.planSelect?.value) url.searchParams.set('selling_plan', this.planSelect.value);
        else url.searchParams.delete('selling_plan');
        history.replaceState(history.state, '', url);
        const error = this.querySelector('[data-lc-form-error]');
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      }
      this.updateSticky();
    }

    normalizeQuantity() {
      const min = Number(this.quantityInput.min) || 1;
      const step = Number(this.quantityInput.step) || 1;
      const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      let quantity = Number(this.quantityInput.value);
      if (!Number.isFinite(quantity)) quantity = min;
      quantity = min + Math.round((quantity - min) / step) * step;
      quantity = Math.min(max, Math.max(min, quantity));
      this.quantityInput.value = quantity;
      this.querySelector('[data-lc-quantity="minus"]').disabled = quantity <= min;
      this.querySelector('[data-lc-quantity="plus"]').disabled = quantity >= max;
    }

    onClick(event) {
      const mediaButton = event.target.closest('[data-lc-media-target]');
      if (mediaButton && this.contains(mediaButton)) {
        this.showMedia(mediaButton.dataset.lcMediaTarget);
        return;
      }
      const quantityButton = event.target.closest('[data-lc-quantity]');
      if (quantityButton && this.contains(quantityButton)) {
        const direction = quantityButton.dataset.lcQuantity === 'plus' ? 1 : -1;
        this.quantityInput.value = (Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1) + direction * (Number(this.quantityInput.step) || 1);
        this.normalizeQuantity();
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (event.target.closest('[data-lc-sticky-submit]')) {
        if (this.submit.disabled) return;
        this.normalizeQuantity();
        this.form.requestSubmit(this.submit);
      }
    }

    showMedia(id) {
      const target = this.querySelector(`[data-lc-media="${id}"]`);
      if (!target) return;
      this.querySelectorAll('[data-lc-media]').forEach((panel) => {
        const active = panel === target;
        panel.hidden = !active;
        if (!active) panel.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.querySelectorAll('[data-lc-media-target]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.lcMediaTarget === String(id)));
      });
    }

    observePurchaseButton() {
      if (!this.sticky) return;
      const header = document.querySelector('[data-lc-header]');
      const headerSection = header?.closest('.lc-header-section');
      const headerPosition = headerSection ? getComputedStyle(headerSection).position : '';
      const headerOffset = ['sticky', 'fixed'].includes(headerPosition) ? Math.ceil(header?.getBoundingClientRect().height || 0) : 0;
      const updateVisibility = () => {
        const rect = this.submit.getBoundingClientRect();
        this.submitVisible = rect.top >= headerOffset && rect.bottom <= window.innerHeight;
        this.updateSticky();
      };
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.submitVisible = entries[0].isIntersecting && entries[0].intersectionRatio >= .95;
          this.updateSticky();
        }, { threshold: [0, .95, 1], rootMargin: `-${headerOffset}px 0px 0px 0px` });
        this.intersectionObserver.observe(this.submit);
      } else {
        window.addEventListener('scroll', updateVisibility, { ...this.listenerOptions, passive: true });
        window.addEventListener('resize', updateVisibility, this.listenerOptions);
      }
      updateVisibility();
    }

    updateSticky() {
      if (!this.sticky) return;
      this.sticky.hidden = this.cartOpen || this.submitVisible;
      const adding = this.form.hasAttribute('data-lc-adding') || this.submit.hasAttribute('data-lc-cart-loading');
      this.stickySubmit.disabled = this.submit.disabled;
      this.stickySubmit.setAttribute('aria-busy', String(adding));
      this.querySelector('[data-lc-sticky-label]').textContent = adding ? this.strings.adding : this.submit.textContent.trim();
      const quantity = Math.max(Number(this.quantityInput.min) || 1, Number(this.quantityInput.value) || 1);
      this.querySelector('[data-lc-sticky-price]').textContent = this.money(this.currentPrice * quantity);
    }

    setupRecipient() {
      const recipient = this.querySelector('[data-lc-recipient]');
      if (!recipient) return;
      const fields = recipient.querySelector('[data-lc-recipient-fields]');
      recipient.addEventListener('toggle', () => {
        fields.disabled = !recipient.open;
      }, this.listenerOptions);
      recipient.querySelector('[data-lc-recipient-offset]').value = new Date().getTimezoneOffset();
      const date = recipient.querySelector('[data-lc-recipient-date]');
      const today = new Date();
      const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
      date.min = localDate.toISOString().slice(0, 10);
      localDate.setDate(localDate.getDate() + 90);
      date.max = localDate.toISOString().slice(0, 10);
    }

    restoreFromUrl() {
      const url = new URL(window.location.href);
      const variantId = url.searchParams.get('variant');
      if (variantId && this.variants.some((variant) => String(variant.id) === variantId)) this.variantInput.value = variantId;
      if (this.planSelect) this.planSelect.value = url.searchParams.get('selling_plan') || '';
      this.updateVariant(false);
    }
  });
}
